import { useState, ReactNode } from "react";
import { useTranslation } from "next-i18next";
import { useWatchlistWikifolioToggleQuery } from "src/api/client/watchlist.api";
import { WfConfirmDialog } from "src/components-mui/common/dialog/wf-confirm-dialog";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { ToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";

interface IUseWatchlistToggleParams {
    wikifolioId: string;
    wikifolioShortDescription: string;
    isOnWatchlist?: boolean;
    addedToWatchlistMessage?: string;
    removedFromWatchlistMessage?: string;
}

interface IUseWatchlistToggleResult {
    isOnWatchlist: boolean;
    loading: boolean;
    onClick: () => void;

    dialog: ReactNode;
}

export const useWatchlistToggle = ({
    wikifolioId,
    wikifolioShortDescription,
    isOnWatchlist: isOnWatchlistFromServer = false,
    addedToWatchlistMessage,
    removedFromWatchlistMessage,
}: IUseWatchlistToggleParams): IUseWatchlistToggleResult => {
    const { t } = useTranslation("common");
    const { openLoginModal } = useLinkBuilder();
    const { isLoggedIn } = useUserStore();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const toast = useWfToast();

    const { isOnWatchlist, isFetching, toggleWatchlist } = useWatchlistWikifolioToggleQuery({ wikifolioId, isOnWatchlistFromServer });

    const runToggle = async () => {
        const wasOnWatchlist = isOnWatchlist;
        const result = await toggleWatchlist();

        if (!result) {
            return;
        }

        const { isError, error } = result;

        if (isError) {
            return toast({
                status: ToastStatus.Error,
                title: error?.message || t("general-error"),
            });
        }

        toast({
            status: ToastStatus.Success,
            title: wasOnWatchlist
                ? removedFromWatchlistMessage ?? t("remove-wikifolio-success")
                : addedToWatchlistMessage ?? t("add-wikifolio-success"),
        });
    };

    const onClick = () => {
        if (!isLoggedIn) {
            void openLoginModal();
            return;
        }

        if (isOnWatchlist) {
            setIsDialogOpen(true);
        } else {
            void runToggle();
        }
    };

    const handleConfirmRemove = () => {
        setIsDialogOpen(false);
        void runToggle();
    };

    const dialog = (
        <WfConfirmDialog
            showCloseButton={false}
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            onConfirm={handleConfirmRemove}
            confirmButtonText={t("remove")}
            confirmButtonProps={{ className: "gtm-watchlist__remove" }}
            wikifolioShortDescription={wikifolioShortDescription}
            header={t("remove-watchlist-header")}
            body={t("remove-watchlist-body")}
        />
    );

    return {
        isOnWatchlist,
        loading: isFetching,
        onClick,
        dialog,
    };
};
